import type { SummaryStatus } from "../global";

/**
 * Pure message-selection helper for the summary error-reporting surfaces
 * (`SummaryStatusIndicator` and `SummaryDialog`).
 *
 * This module is the single source of truth for the whitespace-trimming and
 * fallback rules shared by both surfaces, keeping the React components thin
 * and making the branching logic unit- and property-testable in isolation.
 *
 * Corresponds to design.md §"Components and Interfaces / Why a pure helper for
 * message selection" and §"Data Models / Message-selection decision tables".
 *
 * All exported functions are total: they never throw and always return a
 * well-defined value for every input.
 *
 * _Requirements: 7.2, 7.3, 7.4, 9.2, 9.3, 9.4, 9.5, 9.6, 9.7, 11.1, 11.2,
 * 11.3, 11.4, 11.5, 12.5_
 */

/**
 * Shown in the dialog when the summary generated successfully but contains no
 * displayable topics. Exact PT-BR string from design.md.
 */
export const EMPTY_SUCCESS_MESSAGE =
  "O sumário foi gerado mas não contém dados. Tente gerar novamente.";

/**
 * Shown in the dialog when there is no usable error message and the status is
 * not `success`. Exact PT-BR string from design.md.
 */
export const GENERIC_FALLBACK_MESSAGE =
  "Ocorreu um problema ao processar o sumário. Tente gerar novamente.";

/**
 * Shown in the status indicator when the status is `error` but no usable
 * backend error message is available. Exact PT-BR string from design.md.
 */
export const INDICATOR_FIXED_FALLBACK =
  "Não foi possível gerar o sumário executivo.";

/**
 * The kind of contextual message the dialog should render.
 *
 * - `"error"`           — a backend-provided error message (rendered verbatim).
 * - `"empty-success"`   — summary succeeded but is empty.
 * - `"generic-fallback"`— no usable message and status is not `success`.
 * - `"none"`            — topics exist, no contextual message is shown.
 */
export type DialogMessageKind =
  | "error"
  | "empty-success"
  | "generic-fallback"
  | "none";

/**
 * Result of {@link selectDialogMessage}: the chosen message kind and the exact
 * text to render. `text` is the empty string when `kind === "none"`.
 */
export interface DialogMessageResult {
  kind: DialogMessageKind;
  text: string;
}

/**
 * Normalizes a raw error message into a displayable string or `undefined`.
 *
 * Trims leading/trailing whitespace and collapses `null`, `undefined`, the
 * empty string, and whitespace-only strings to `undefined`.
 *
 * _Requirements: 2.4, 8.2, 8.3, 10.3_
 */
export function normalizeErrorMessage(
  raw: string | null | undefined
): string | undefined {
  if (typeof raw !== "string") {
    return undefined;
  }

  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Selects the text the `SummaryStatusIndicator` renders while the status is
 * `error`.
 *
 * Returns the trimmed message when it contains at least one non-whitespace
 * character, otherwise the fixed fallback. The returned text is always
 * non-empty.
 *
 * _Requirements: 7.2, 7.3, 7.4, 11.1, 11.4, 12.5_
 */
export function selectIndicatorErrorText(
  errorMessage: string | undefined
): string {
  return normalizeErrorMessage(errorMessage) ?? INDICATOR_FIXED_FALLBACK;
}

/**
 * Selects which contextual message the `SummaryDialog` should render.
 *
 * Decision table (design.md §"Message-selection decision tables"):
 * - `topicCount >= 1`                              → `none` (empty text).
 * - `topicCount === 0` and `status === "success"`  → `empty-success`.
 * - `topicCount === 0`, status not `success`, with a non-empty trimmed
 *   message                                        → `error` (verbatim message).
 * - `topicCount === 0`, status not `success`, with an empty/undefined
 *   message                                        → `generic-fallback`.
 *
 * Never returns the legacy bare text "Nenhum sumário disponível".
 *
 * _Requirements: 9.2, 9.3, 9.4, 9.5, 9.6, 9.7, 11.2, 11.3, 11.4, 11.5_
 */
export function selectDialogMessage(input: {
  topicCount: number;
  summaryStatus: SummaryStatus;
  errorMessage: string | undefined;
}): DialogMessageResult {
  const { topicCount, summaryStatus, errorMessage } = input;

  if (topicCount >= 1) {
    return { kind: "none", text: "" };
  }

  if (summaryStatus === "success") {
    return { kind: "empty-success", text: EMPTY_SUCCESS_MESSAGE };
  }

  const normalized = normalizeErrorMessage(errorMessage);
  if (normalized) {
    return { kind: "error", text: normalized };
  }

  return { kind: "generic-fallback", text: GENERIC_FALLBACK_MESSAGE };
}

/**
 * Maps a resolved summary query status and a model `errorMessage` value to the
 * exact value the `ReportDetailsContainer` dispatches to the store's
 * `setSummaryErrorMessage` action.
 *
 * Returns the normalized (trimmed, non-empty) message when the status is
 * `"error"` and the message is non-empty, and `null` in every other case
 * (non-`error` status, or `error` status with a nullish/empty/whitespace
 * message). This is the single source of truth for the container's dispatch
 * mapping, keeping the sync effect(s) thin and the logic property-testable in
 * isolation.
 *
 * Total: never throws for any input.
 *
 * _Requirements: 6.1, 6.2, 6.3_
 */
export function selectContainerErrorMessage(
  queryStatus: SummaryStatus,
  modelErrorMessage: string | null | undefined
): string | null {
  const normalized = normalizeErrorMessage(modelErrorMessage);
  return queryStatus === "error" && normalized ? normalized : null;
}
